/**
 * Enhanced OpenRouter Service with Robust Rate Limit Handling
 * Handles rate limits, auto-fallbacks, retries, and queueing
 */

export const modelConstants = {
  // Expert models for different tasks (ordered by reliability/availability)
  expertModels: {
    general: "mistralai/mistral-nemo:free", // More reliable free model
    coding: "qwen/qwen-2.5-72b-instruct:free",
    creative: "mistralai/mistral-nemo:free",
    analyst: "qwen/qwen-2.5-72b-instruct:free",
    vision: "meta-llama/llama-3.2-11b-vision-instruct:free",
    fast: "meta-llama/llama-3.2-3b-instruct:free",
    multilingual: "qwen/qwen-2.5-72b-instruct:free"
  },

  // Model tiers ordered by availability (most reliable first)
  modelTiers: {
    tier1: [
      "meta-llama/llama-3.2-3b-instruct:free",
      "mistralai/mistral-7b-instruct:free",
      "google/gemma-2-9b-it:free"
    ],
    tier2: [
      "mistralai/mistral-nemo:free",
      "qwen/qwen-2.5-72b-instruct:free",
      "meta-llama/llama-3.2-11b-vision-instruct:free"
    ],
    tier3: [
      "meta-llama/llama-3.1-405b-instruct:free"
    ]
  },

  // Model specializations with fallback ordering
  modelSpecializations: {
    general: [
      "mistralai/mistral-nemo:free",
      "qwen/qwen-2.5-72b-instruct:free", 
      "meta-llama/llama-3.2-3b-instruct:free",
      "mistralai/mistral-7b-instruct:free",
      "google/gemma-2-9b-it:free",
      "meta-llama/llama-3.1-405b-instruct:free"
    ],
    coding: [
      "qwen/qwen-2.5-72b-instruct:free",
      "mistralai/mistral-nemo:free",
      "meta-llama/llama-3.2-3b-instruct:free",
      "mistralai/mistral-7b-instruct:free",
      "meta-llama/llama-3.1-405b-instruct:free"
    ],
    vision: [
      "meta-llama/llama-3.2-11b-vision-instruct:free"
    ],
    creative: [
      "mistralai/mistral-nemo:free",
      "qwen/qwen-2.5-72b-instruct:free",
      "meta-llama/llama-3.2-3b-instruct:free",
      "meta-llama/llama-3.1-405b-instruct:free"
    ],
    reasoning: [
      "qwen/qwen-2.5-72b-instruct:free",
      "mistralai/mistral-nemo:free",
      "meta-llama/llama-3.1-405b-instruct:free",
      "meta-llama/llama-3.2-3b-instruct:free"
    ],
    multilingual: [
      "qwen/qwen-2.5-72b-instruct:free",
      "mistralai/mistral-nemo:free",
      "meta-llama/llama-3.2-3b-instruct:free",
      "meta-llama/llama-3.1-405b-instruct:free"
    ],
    fast: [
      "meta-llama/llama-3.2-3b-instruct:free",
      "mistralai/mistral-7b-instruct:free",
      "google/gemma-2-9b-it:free",
      "mistralai/mistral-nemo:free"
    ]
  },

  // Model info for display
  modelInfo: {
    "meta-llama/llama-3.1-405b-instruct:free": {
      name: "Llama 3.1 405B Instruct",
      description: "Meta's flagship model - High demand, limited availability",
      strengths: "Top-tier reasoning, coding, creative writing, complex analysis",
      context: "66K tokens",
      provider: "Meta",
      tier: 3,
      reliability: "Low" // High demand
    },
    "qwen/qwen-2.5-72b-instruct:free": {
      name: "Qwen2.5 72B Instruct",
      description: "Advanced model with excellent coding and math capabilities",
      strengths: "Superior coding & math, structured data, JSON output",
      context: "33K tokens",
      provider: "Qwen",
      tier: 2,
      reliability: "Medium"
    },
    "meta-llama/llama-3.2-11b-vision-instruct:free": {
      name: "Llama 3.2 11B Vision",
      description: "Multimodal model with visual understanding",
      strengths: "Image analysis, visual Q&A, image captioning",
      context: "131K tokens",
      provider: "Meta",
      tier: 2,
      reliability: "Medium"
    },
    "mistralai/mistral-nemo:free": {
      name: "Mistral Nemo 12B",
      description: "Reliable 12B model with good availability",
      strengths: "Function calling, multilingual, balanced performance",
      context: "131K tokens",
      provider: "Mistral",
      tier: 2,
      reliability: "High"
    },
    "google/gemma-2-9b-it:free": {
      name: "Gemma 2 9B",
      description: "Google's efficient model with high availability",
      strengths: "High efficiency, cost-effective, reliable",
      context: "8K tokens",
      provider: "Google",
      tier: 1,
      reliability: "Very High"
    },
    "meta-llama/llama-3.2-3b-instruct:free": {
      name: "Llama 3.2 3B Instruct",
      description: "Fast, compact model with excellent availability",
      strengths: "Speed, efficiency, multilingual, highly available",
      context: "131K tokens",
      provider: "Meta",
      tier: 1,
      reliability: "Very High"
    },
    "mistralai/mistral-7b-instruct:free": {
      name: "Mistral 7B Instruct",
      description: "Reliable 7B model with consistent availability",
      strengths: "Fast responses, reliable, good general performance",
      context: "33K tokens",
      provider: "Mistral",
      tier: 1,
      reliability: "Very High"
    }
  },

  // Get fallback models for a given category
  getFallbackModels: (category = 'general') => {
    return modelConstants.modelSpecializations[category] || modelConstants.modelSpecializations.general;
  },

  // Get most reliable model for a category
  getMostReliableModel: (category = 'general') => {
    const models = modelConstants.getFallbackModels(category);
    return models[0]; // First model is most reliable
  }
};

// Rate limit tracker
class RateLimitTracker {
  constructor() {
    this.limits = new Map();
    this.requests = new Map();
  }

  isRateLimited(model) {
    const now = Date.now();
    const requests = this.requests.get(model) || [];
    
    // Clean old requests (older than 1 minute)
    const recentRequests = requests.filter(time => now - time < 60000);
    this.requests.set(model, recentRequests);
    
    // Check if we've hit the limit
    const limit = this.limits.get(model) || 1; // Default 1 per minute for free tier
    return recentRequests.length >= limit;
  }

  recordRequest(model) {
    const now = Date.now();
    const requests = this.requests.get(model) || [];
    requests.push(now);
    this.requests.set(model, requests);
  }

  setLimit(model, limit) {
    this.limits.set(model, limit);
  }

  getNextAvailableTime(model) {
    const requests = this.requests.get(model) || [];
    if (requests.length === 0) return 0;
    
    const oldestRequest = Math.min(...requests);
    const nextAvailable = oldestRequest + 60000; // 1 minute later
    return Math.max(0, nextAvailable - Date.now());
  }
}

// Request queue for handling rate limits
class RequestQueue {
  constructor() {
    this.queue = [];
    this.processing = false;
  }

  async add(requestFn, priority = 0) {
    return new Promise((resolve, reject) => {
      this.queue.push({ requestFn, resolve, reject, priority });
      this.queue.sort((a, b) => b.priority - a.priority); // Higher priority first
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.processing || this.queue.length === 0) return;

    this.processing = true;
    
    while (this.queue.length > 0) {
      const { requestFn, resolve, reject } = this.queue.shift();
      
      try {
        const result = await requestFn();
        resolve(result);
      } catch (error) {
        reject(error);
      }
      
      // Add small delay between requests
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    this.processing = false;
  }
}

// Global instances
const rateLimitTracker = new RateLimitTracker();
const requestQueue = new RequestQueue();

const OPENROUTER_API_URL = "https://openrouter.ai/api/v1/chat/completions";

// Enhanced error parser
const parseOpenRouterError = (error) => {
  if (typeof error === 'string') {
    if (error.includes('Rate limit exceeded')) {
      return {
        type: 'rate_limit',
        message: error,
        retryAfter: 60000 // 1 minute
      };
    }
    if (error.includes('quota exceeded') || error.includes('insufficient credits')) {
      return {
        type: 'quota_exceeded',
        message: error
      };
    }
    if (error.includes('model not found') || error.includes('invalid model')) {
      return {
        type: 'invalid_model',
        message: error
      };
    }
  }
  
  return {
    type: 'unknown',
    message: error.message || error.toString()
  };
};

// Enhanced send message with automatic fallbacks and retries
export const sendMessage = async (
  apiKey,
  model,
  messages,
  extendedThinking = false,
  tokenLimit = 20000,
  options = {}
) => {
  const {
    maxRetries = 3,
    fallbackEnabled = true,
    category = 'general',
    onModelChange = null,
    onRetry = null
  } = options;

  if (!apiKey || !apiKey.trim()) {
    throw new Error("OpenRouter API key is required");
  }

  // Get fallback models
  const fallbackModels = fallbackEnabled ? modelConstants.getFallbackModels(category) : [model];
  const modelsToTry = fallbackModels.includes(model) ? 
    [model, ...fallbackModels.filter(m => m !== model)] : 
    [model, ...fallbackModels];

  let lastError = null;
  
  for (let modelIndex = 0; modelIndex < modelsToTry.length; modelIndex++) {
    const currentModel = modelsToTry[modelIndex];
    
    // Skip if rate limited and not the last option
    if (rateLimitTracker.isRateLimited(currentModel) && modelIndex < modelsToTry.length - 1) {
      console.log(`Skipping rate-limited model: ${currentModel}`);
      continue;
    }

    for (let retry = 0; retry < maxRetries; retry++) {
      try {
        // Record the request attempt
        rateLimitTracker.recordRequest(currentModel);
        
        // Notify about model change
        if (currentModel !== model && onModelChange) {
          onModelChange(currentModel, modelIndex > 0 ? 'fallback' : 'retry');
        }

        // Notify about retry
        if (retry > 0 && onRetry) {
          onRetry(retry, currentModel);
        }

        const response = await fetch(OPENROUTER_API_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiKey}`,
            "HTTP-Referer": window.location.origin,
            "X-Title": "AI Free Chat"
          },
          body: JSON.stringify({
            model: currentModel,
            messages: messages,
            temperature: 0.7,
            max_tokens: tokenLimit,
            stream: true,
            thinking: extendedThinking
          })
        });

        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch (jsonError) {
            throw new Error(`API request failed with status ${response.status}`);
          }

          const parsedError = parseOpenRouterError(errorData.error?.message || `HTTP ${response.status}`);
          
          if (parsedError.type === 'rate_limit') {
            console.log(`Rate limit hit for ${currentModel}, trying next model...`);
            
            // Set rate limit for this model
            rateLimitTracker.setLimit(currentModel, 1);
            
            // If this is not our last model, try the next one
            if (modelIndex < modelsToTry.length - 1) {
              break; // Break retry loop, try next model
            }
            
            // If this is our last model, wait and retry
            if (retry < maxRetries - 1) {
              const waitTime = Math.min(60000, Math.pow(2, retry) * 1000); // Exponential backoff, max 1 minute
              console.log(`Waiting ${waitTime}ms before retry...`);
              await new Promise(resolve => setTimeout(resolve, waitTime));
              continue;
            }
          }
          
          if (parsedError.type === 'quota_exceeded') {
            // Skip this model entirely
            console.log(`Quota exceeded for ${currentModel}, trying next model...`);
            break;
          }
          
          throw new Error(parsedError.message);
        }

        // Success! Return the response
        console.log(`Successfully using model: ${currentModel}`);
        return response;

      } catch (error) {
        lastError = error;
        console.error(`Attempt ${retry + 1} failed for ${currentModel}:`, error.message);
        
        const parsedError = parseOpenRouterError(error.message);
        
        // If it's a rate limit error and we have more models to try, break to next model
        if (parsedError.type === 'rate_limit' && modelIndex < modelsToTry.length - 1) {
          break;
        }
        
        // If it's the last retry for this model, try next model
        if (retry === maxRetries - 1) {
          break;
        }
        
        // Wait before retry (exponential backoff)
        const waitTime = Math.min(5000, Math.pow(2, retry) * 1000);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
  }

  // All models and retries failed
  throw new Error(
    lastError?.message || 
    "All available models are currently unavailable. Please try again in a few minutes."
  );
};

// Enhanced queued send message
export const sendMessageQueued = async (
  apiKey,
  model,
  messages,
  extendedThinking = false,
  tokenLimit = 20000,
  options = {}
) => {
  const priority = options.priority || 0;
  
  return requestQueue.add(async () => {
    return sendMessage(apiKey, model, messages, extendedThinking, tokenLimit, options);
  }, priority);
};

// Utility functions
export const getAvailableModels = () => {
  const allModels = Object.keys(modelConstants.modelInfo);
  return allModels.map(modelId => ({
    id: modelId,
    ...modelConstants.modelInfo[modelId],
    isRateLimited: rateLimitTracker.isRateLimited(modelId),
    nextAvailable: rateLimitTracker.getNextAvailableTime(modelId)
  }));
};

export const getBestAvailableModel = (category = 'general') => {
  const models = modelConstants.getFallbackModels(category);
  
  for (const model of models) {
    if (!rateLimitTracker.isRateLimited(model)) {
      return model;
    }
  }
  
  // If all are rate limited, return the one that will be available soonest
  let bestModel = models[0];
  let shortestWait = rateLimitTracker.getNextAvailableTime(models[0]);
  
  for (let i = 1; i < models.length; i++) {
    const waitTime = rateLimitTracker.getNextAvailableTime(models[i]);
    if (waitTime < shortestWait) {
      shortestWait = waitTime;
      bestModel = models[i];
    }
  }
  
  return bestModel;
};

export const getModelStatus = (modelId) => {
  return {
    isRateLimited: rateLimitTracker.isRateLimited(modelId),
    nextAvailable: rateLimitTracker.getNextAvailableTime(modelId),
    reliability: modelConstants.modelInfo[modelId]?.reliability || 'Unknown'
  };
};

// Export the tracker for external use
export { rateLimitTracker, requestQueue };